import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router';
import moment from 'moment';

import { useGetInvestmentFundQuery, useUpdateInvestmentFundMutation } from 'api/main';
import { texts } from 'utils/languages/fr';

import { MenuPath } from 'components/ActionsMenu/actions';
import { ActionsMenu, Loader, NativeDatePicker, StyledTableRow } from 'components';
import { Box, Checkbox, IconButton, TableCell } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    input: {
        padding: 0,
        margin: 0,
        height: '100%',
        width: '100%',
    },
}));

const PathAction = React.forwardRef((pr, ref) => <MenuPath ref={ref} {...pr} />);

const EditableDateCell = ({ name, initialDate, fund }) => {
    const classes = useStyles();
    const [date, setDate] = useState(initialDate);
    const [loading, setLoading] = useState(false);
    const [updateInvestmentFund, { isError, isSuccess }] = useUpdateInvestmentFundMutation();

    const handleSaveDate = useCallback(() => {
        setLoading(true); // need a local loader flag
        updateInvestmentFund({
            id: fund.id,
            [name]: date.utcOffset(0).startOf('day'),
        });
    }, [fund, date, name, updateInvestmentFund]);

    useEffect(() => {
        if (isSuccess || isError) setLoading(false);
    }, [isSuccess, isError]);

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" height="100%">
            <NativeDatePicker
                className={classes.input}
                disableFuture={false}
                handleDateChange={newDate => setDate(newDate)}
                value={date}
            />
            <Loader loading={loading} mt={0} size={20}>
                <IconButton
                    disableRipple
                    size="small"
                    color="primary"
                    disabled={initialDate ? initialDate.isSame(date, 'day') : null === date}
                    onClick={handleSaveDate}
                >
                    <icons.ContentSave />
                </IconButton>
            </Loader>
        </Box>
    );
};

EditableDateCell.propTypes = {
    fund: PropTypes.object,
    name: PropTypes.string,
    initialDate: PropTypes.object,
};

const FundRow = ({ fund }) => {
    const { id, name, type, visible, enabled, commissionControlDate, releaseDate, closingDate } =
        fund;
    const { path } = useRouteMatch();

    const { data: currentFund } = useGetInvestmentFundQuery(fund?.id, { skip: !fund?.id });
    const [updateInvestmentFund, { isLoading }] = useUpdateInvestmentFundMutation();

    const handleVisibilityChange = useCallback(
        (id, visible) => () => {
            updateInvestmentFund({ id, visible });
        },
        [updateInvestmentFund]
    );

    const handlePrivacyChange = useCallback(
        (id, privacy) => () => {
            updateInvestmentFund({ id, private: privacy });
        },
        [updateInvestmentFund]
    );

    const handleAvailabilityChange = useCallback(
        (id, enabled) => () => {
            updateInvestmentFund({ id, enabled });
        },
        [updateInvestmentFund]
    );

    return (
        <StyledTableRow>
            <TableCell>{name}</TableCell>
            <TableCell>{type}</TableCell>
            <TableCell padding="none" align="center">
                <EditableDateCell
                    name="releaseDate"
                    initialDate={releaseDate ? moment(releaseDate) : null}
                    fund={fund}
                />
            </TableCell>
            <TableCell padding="none" align="center">
                <EditableDateCell
                    name="commissionControlDate"
                    initialDate={commissionControlDate ? moment(commissionControlDate) : null}
                    fund={fund}
                />
            </TableCell>
            <TableCell>{closingDate ? moment(closingDate).format('L') : '-'}</TableCell>
            <TableCell align="center">
                <Checkbox
                    onChange={handleAvailabilityChange(id, !enabled)}
                    value={enabled}
                    checked={enabled}
                    disabled={isLoading}
                />
            </TableCell>
            <TableCell align="center">
                <Checkbox
                    onChange={handleVisibilityChange(id, !visible)}
                    value={visible}
                    checked={visible}
                    disabled={isLoading}
                />
            </TableCell>
            <TableCell align="center">
                <Checkbox
                    onChange={handlePrivacyChange(id, !fund.private)}
                    value={fund.private}
                    checked={fund.private}
                    disabled={isLoading}
                />
            </TableCell>
            <TableCell>
                <ActionsMenu autoClose>
                    {currentFund?.private && (
                        <PathAction
                            path={`${path}/${id}/reserved`}
                            label={texts.PRIVATE_FUNDS_FOR_DISTRIBUTORS}
                            Icon={icons.FolderLockOutline}
                        />
                    )}
                    {currentFund?.evergreen && (
                        <PathAction
                            path={`${path}/${id}/vl-management`}
                            label={texts.VL_MANAGEMENT}
                            Icon={icons.TableEdit}
                        />
                    )}
                    <PathAction
                        path={`${path}/${id}/attributes`}
                        label={texts.FUND_ATTRIBUTES}
                        Icon={icons.FormSelect}
                    />
                    <PathAction
                        path={`${path}/${id}/tunnels`}
                        label={texts.TUNNEL_VERSIONS}
                        Icon={icons.TunnelOutline}
                    />
                </ActionsMenu>
            </TableCell>
        </StyledTableRow>
    );
};

FundRow.propTypes = {
    fund: PropTypes.object,
};

export default FundRow;
